'use client'
import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import Button from '@/components/discardedComponent/Button'
import Modal from './index'
import cls from 'classnames'

type TListener = () => void
export type TDialogData = {
  content:
    | string
    | React.ReactNode
    | React.FC
    | ((props: { close: () => void; confirm: () => void }) => string | React.ReactNode)
  extra?: string | React.ReactNode
  type?: 'success' | 'error' | 'warning' | 'info'
  confirmText?: string
  cancelText?: string
  title?: string
  width?: number | string
  onClose?: () => void
  onConfirm?: () => void
  onCancel?: () => void
  showClose?: boolean
  showFooter?: boolean
  showCancel?: boolean
  showConfirm?: boolean
  className?: string
  alignTitle?: 'left' | 'center'
}
let modals: React.ReactNode[] = []
let listeners: TListener[] = []

const dialogStore = {
  addModal(modal: React.ReactNode) {
    modals = [...modals, modal]
    emitChange()
    return () => {
      modals = modals.filter((m) => m !== modal)
      emitChange()
    }
  },
  subscribe(listener: TListener) {
    listeners = [...listeners, listener]
    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  },
  getSnapshot() {
    return modals
  },
  getServerSnapshot() {
    return modals
  },
}

function emitChange() {
  for (const listener of listeners) {
    listener()
  }
}

export function DialogHolder() {
  return React.useSyncExternalStore<React.ReactNode[]>(
    dialogStore.subscribe,
    dialogStore.getSnapshot,
    dialogStore.getServerSnapshot
  )
}

const dialogAnimationDuration = 200

const isPromise = (obj: void | null) => {
  return Object.prototype.toString.call(obj) === '[object Promise]'
}

export const dialog = (dialogData: TDialogData) => {
  const {
    showFooter = true,
    showCancel = true,
    showConfirm = true,
    showClose = false,
    width = 400,
    confirmText,
    cancelText,
    onCancel,
    onConfirm,
    className,
    alignTitle = 'left',
  } = dialogData
  return new Promise<{ isConfirmed: boolean }>((resolve) => {
    const Dialog = () => {
      const [visible, setVisible] = useState(false)
      useEffect(() => {
        setVisible(true)
      }, [])

      const handleConfirm = async () => {
        const result = onConfirm ? onConfirm() : null
        if (isPromise(result)) {
          await result
        }

        setVisible(false)
        setTimeout(() => {
          // onConfirm?.()
          resolve({ isConfirmed: true })
          dialogData.onClose?.()
          removeModal()
        }, dialogAnimationDuration)
      }

      const handleCancel = () => {
        setVisible(false)
        setTimeout(() => {
          onCancel?.()
          resolve({ isConfirmed: false })
          dialogData.onClose?.()
          removeModal()
        }, dialogAnimationDuration)
      }

      return (
        <Modal
          extra={dialogData.extra}
          width={Number(dialogData.width) || 400}
          title={dialogData.title}
          alignTitle={alignTitle}
          open={visible}
          onClose={handleCancel}
          className={className}
          transitionDuration={dialogAnimationDuration}
          showCloseBtn={showClose}
          footer={
            showFooter && (
              <div className={styles.modalFooter}>
                {showCancel && (
                  <Button type="secondary" onClick={handleCancel}>
                    {cancelText || 'Cancel'}
                  </Button>
                )}
                {showConfirm && <Button onClick={handleConfirm}>{confirmText || 'Confirm'}</Button>}
              </div>
            )
          }
        >
          <div className={`pl-8 pr-8 ${styles.dialogContent}`}>
            {typeof dialogData.content === 'function'
              ? dialogData.content({ close: handleCancel, confirm: handleConfirm })
              : dialogData.content}
          </div>
        </Modal>
      )
    }

    const removeModal = dialogStore.addModal(<Dialog key={dialogStore.getSnapshot().length} />)
  })
}

export const alert = (dialogData: Omit<TDialogData, 'showCancel'>) => {
  return dialog({
    ...dialogData,
    showCancel: false,
  })
}

export const info = (dialogData: Omit<TDialogData, 'showCancel'>) => {
  return dialog({
    ...dialogData,
    showCancel: false,
    content: (
      <div className={styles.info}>
        {/* 样式待优化 */}
        {dialogData.content}
      </div>
    ),
  })
}

export const success = (dialogData: Omit<TDialogData, 'showCancel'>) => {
  return dialog({
    ...dialogData,
    showCancel: false,
    content: (
      <div className={styles.success}>
        {/* 样式待优化 */}
        {dialogData.content}
      </div>
    ),
  })
}

export const warning = (dialogData: Omit<TDialogData, 'showCancel'>) => {
  return dialog({
    ...dialogData,
    showCancel: false,
    content: (
      <div className={styles.warning}>
        {/* 样式待优化 */}
        {dialogData.content}
      </div>
    ),
  })
}

export const error = (dialogData: Omit<TDialogData, 'showCancel'>) => {
  return dialog({
    ...dialogData,
    showCancel: false,
    content: (
      <div className={styles.error}>
        {/* 样式待优化 */}
        {dialogData.content}
      </div>
    ),
  })
}

export const confirm = dialog

export default dialog
